import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

type MessageType = 'support' | 'feedback';

const FeedbackPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<MessageType>('support');

  // Support form
  const [issueType, setIssueType] = useState('');
  const [supportMessage, setSupportMessage] = useState('');

  // Feedback form
  const [rating, setRating] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleSubmitSupport = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!issueType || !supportMessage.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (!user) {
      setError('You must be logged in to submit');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'supportTickets'), {
        userId: user.uid,
        userEmail: user.email,
        type: 'support',
        issueType: issueType,
        message: supportMessage.trim(),
        status: 'open',
        createdAt: serverTimestamp(),
      });

      setSuccess("Support ticket submitted! We'll get back to you soon.");
      setIssueType('');
      setSupportMessage('');

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error submitting support:', err);
      setError('Failed to submit. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (rating === 0 || !feedbackMessage.trim()) {
      setError('Please provide a rating and feedback');
      return;
    }

    if (!user) {
      setError('You must be logged in to submit');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'testimonials'), {
        userId: user.uid,
        userName: isAnonymous ? 'Anonymous' : user.displayName || 'Anonymous',
        userEmail: isAnonymous ? null : user.email,
        rating: rating,
        reviewText: feedbackMessage.trim(),
        isAnonymous: isAnonymous,
        isFeatured: false, // Will be updated by AI
        status: 'pending', // Will be updated by AI
        submittedAt: serverTimestamp(),
      });

      setSuccess(
        'Thank you for your feedback! Our AI will review it shortly and it may appear on our website.'
      );
      setRating(0);
      setFeedbackMessage('');
      setIsAnonymous(false);

      setTimeout(() => setSuccess(''), 7000);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Support and Feedback
      </h2>

      {/* Tab Toggle */}
      <div className="flex space-x-2 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('support')}
          className={`px-6 py-2 rounded-lg font-medium transition ${
            activeTab === 'support'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Support
        </button>
        <button
          onClick={() => setActiveTab('feedback')}
          className={`px-6 py-2 rounded-lg font-medium transition ${
            activeTab === 'feedback'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Feedback / Testimonial
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Support Form */}
      {activeTab === 'support' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Support</h3>
          <p className="text-sm text-gray-600 mb-6">
            Please fill out the form and let us know what we can help you with
            through email
          </p>

          <form onSubmit={handleSubmitSupport} className="space-y-4">
            {/* Type of Issue */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type of issue
              </label>
              <input
                type="text"
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                placeholder="e.g. Payment issue, App not working, etc."
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                disabled={submitting}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100"
                placeholder="Describe your issue..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      )}

      {/* Feedback Form */}
      {activeTab === 'feedback' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Feedback / Testimonial
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Give us a rating and let us know what you think! We use AI to
            analyze feedback quality and may feature yours on our website.
          </p>

          {/* AI Info Box */}
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">AI-Powered Review</p>
                <p>
                  Your feedback will be automatically analyzed for sentiment and
                  quality. High-quality reviews may be featured on our landing
                  page to help others learn about Unveil.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmitFeedback} className="space-y-4">
            {/* Star Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating
              </label>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    disabled={submitting}
                    className="focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      className={`w-8 h-8 transition-colors ${
                        star <= rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                      />
                    </svg>
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {rating === 5 && '⭐ Excellent!'}
                  {rating === 4 && '⭐ Great!'}
                  {rating === 3 && '⭐ Good'}
                  {rating === 2 && '⭐ Fair'}
                  {rating === 1 && '⭐ Needs improvement'}
                </p>
              )}
            </div>

            {/* Feedback Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feedback / Testimonial
              </label>
              <textarea
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                disabled={submitting}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100"
                placeholder="Share your experience with Unveil... What did you like? How has it helped you?"
              />
            </div>

            {/* Anonymous Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-gray-600 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <div>
                  <label
                    htmlFor="anonymous"
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Submit as Anonymous
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    {isAnonymous
                      ? 'Your feedback will be shown as "Anonymous"'
                      : `Your feedback will be shown as "${user?.displayName || 'Your Name'}"`}
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  id="anonymous"
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  disabled={submitting}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"></div>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;
